import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'src/utils/axios';
import {
    Container, Card, CardHeader, CardContent, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, IconButton, Tooltip, Typography, TextField, InputAdornment, Box,
    CircularProgress, Snackbar, Alert, Slide
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const TURKISH_MONTHS = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const BulletinList = () => {
    const history = useHistory();
    const [bulletins, setBulletins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloadingPdfBankaNo, setDownloadingPdfBankaNo] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchBulletins();
    }, []);

    const fetchBulletins = async (search = '') => {
        setLoading(true);
        try {
            const response = await axios.get(`GetBulletins?SearchText=${search}`);
            setBulletins(response.data);
        } catch (error) {
            console.error("Bültenler çekilirken hata oluştu", error);
            setSnackbar({ open: true, message: 'Bültenler yüklenirken bir hata oluştu.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event) => setSearchTerm(event.target.value);
    const handleKeyDown = (event) => { if (event.key === 'Enter') fetchBulletins(searchTerm); };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const handleOpenPdf = async (id) => {
        if (!id) return;
        setDownloadingPdfBankaNo(id);
        try {
            const response = await axios.get(`GetBulletinPdf?ID=${id}`);
            const pdfString = response.data.PDFstring || response.data;
            if (!pdfString) {
                setSnackbar({ open: true, message: 'PDF dosyası bulunmamaktadır.', severity: 'warning' });
                return;
            }
            const byteCharacters = atob(pdfString);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
            const blob = new Blob([byteNumbers], { type: 'application/pdf' });
            window.open(URL.createObjectURL(blob), '_blank');
        } catch (error) {
            setSnackbar({ open: true, message: 'PDF sunucudan alınamadı.', severity: 'error' });
        } finally {
            setDownloadingPdfBankaNo(null);
        }
    };

    // YENİ DURUM KODLARI (State Machine)
    const getStatusChip = (status) => {
        if (status === 1 || status === '1') return <Chip label="Yayında" color="success" size="small" />;
        if (status === 2 || status === '2') return <Chip label="İlk Onay Bekliyor" color="warning" size="small" />;
        if (status === 3 || status === '3') return <Chip label="Revize Bekliyor" color="error" size="small" />;
        if (status === 5 || status === '5') return <Chip label="Son Onay Bekliyor" color="secondary" size="small" />;
        if (status === 6 || status === '6') return <Chip label="Yayına Hazır" color="info" size="small" />;
        return <Chip label="Taslak" color="default" size="small" />;
    };

    return (
        <Container maxWidth="xl" style={{ paddingTop: '24px', paddingBottom: '24px' }}>
            <Card>
                <CardHeader
                    title="Bülten Yönetimi"
                    subheader="Sistemdeki tüm bültenleri görüntüleyebilir ve yönetebilirsiniz."
                    action={
                        <Box sx={{ width: 300, marginRight: 2, marginTop: 1 }}>
                            <TextField
                                fullWidth size="small" variant="outlined" placeholder="Bülten Kodu Ara..."
                                value={searchTerm} onChange={handleSearchChange} onKeyDown={handleKeyDown}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => fetchBulletins(searchTerm)} edge="end"><SearchIcon /></IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    }
                />
                <Divider />
                <CardContent>
                    <TableContainer component={Paper} variant="outlined">
                        <Table sx={{ minWidth: 650 }} aria-label="bülten tablosu">
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Kod</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Bülten</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Ürün Grubu</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Yıl</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Ay</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Durum</TableCell>
                                    <TableCell align='center' sx={{ fontWeight: 'bold', textAlign: 'center', minWidth: '160px' }}>İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={30} sx={{ my: 2 }} /></TableCell></TableRow>
                                ) : bulletins.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center"><Typography sx={{ py: 2 }} color="textSecondary">Kayıtlı bülten bulunamadı.</Typography></TableCell></TableRow>
                                ) : (
                                    bulletins.map((row, index) => {
                                        const dateObj = row.BULTEN_TARIHI ? new Date(row.BULTEN_TARIHI) : null;
                                        return (
                                            <TableRow key={row.ID || index} hover>
                                                <TableCell><Typography variant="body2" fontWeight="bold">{row.SAYI}</Typography></TableCell>
                                                <TableCell>{row.BULTEN_KATEGORI}</TableCell>
                                                <TableCell>{row.URUN_GRUPLARI}</TableCell>
                                                <TableCell>{dateObj ? dateObj.getFullYear() : '-'}</TableCell>
                                                <TableCell>{dateObj ? TURKISH_MONTHS[dateObj.getMonth() + 1] : '-'}</TableCell>
                                                <TableCell>{getStatusChip(row.DURUM)}</TableCell>
                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title={row.ID ? "PDF Görüntüle" : "Henüz Yayında Değil"}>
                                                        <span>
                                                            <IconButton color="info" disabled={!row.ID || downloadingPdfBankaNo === row.ID} onClick={() => handleOpenPdf(row.ID)}>
                                                                {downloadingPdfBankaNo === row.ID ? <CircularProgress size={24} color="info" /> : <PictureAsPdfIcon />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="İncele / İşlem Yap">
                                                        <IconButton color="primary" onClick={() => history.push({ pathname: '/CreateBulletin', state: { editData: row } })}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
            <Snackbar open={snackbar.open} autoHideDuration={null} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} TransitionComponent={(props) => <Slide {...props} direction="down" />}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', boxShadow: 3 }} style={{ backgroundColor: snackbar.severity === 'error' ? '#c62828' : snackbar.severity === 'success' ? '#2e7d32' : undefined, color: '#fff' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default BulletinList;